/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IWiseTableAdvancedFilter } from 'components/wise/types';

interface IComponentParameters {
  page?: number;
  limit?: number;
  [key: string]: any;
}

interface ITraderPanelParameters {
  [componentSymbol: string]: IComponentParameters;
}

interface ITraderPanelSlice {
  parameters: ITraderPanelParameters;
  wiseTableAdvancedFilters: {
    [componentSymbol: string]: IWiseTableAdvancedFilter[];
  };
  wiseTableColumnVisibility: {
    [componentSymbol: string]: Record<string, boolean>;
  };
  wiseTableColumnOrder: {
    [componentSymbol: string]: string[];
  };
  wiseTableSelectedSettingsPresetId: {
    [componentSymbol: string]: string | null;
  };
}

// TODO: tymczasowa persystencja w localStorage (BE `/api/ui/sales-crm/filters` nie istnieje).
// Trzymamy widocznosc/kolejnosc/aktywny preset tabel, zeby przezylo F5. Docelowo usunac po
// dopisaniu BE i przelaczeniu use{Get,Post,Put}SalesCrmFilters z powrotem na HTTP.
const STORAGE_KEY = 'traderPanelWiseTableState';

interface IPersistedTraderPanelSlice {
  wiseTableColumnVisibility: ITraderPanelSlice['wiseTableColumnVisibility'];
  wiseTableColumnOrder: ITraderPanelSlice['wiseTableColumnOrder'];
  wiseTableSelectedSettingsPresetId: ITraderPanelSlice['wiseTableSelectedSettingsPresetId'];
}

const readPersistedState = (): IPersistedTraderPanelSlice => {
  if (typeof window === 'undefined') {
    return {
      wiseTableColumnVisibility: {},
      wiseTableColumnOrder: {},
      wiseTableSelectedSettingsPresetId: {}
    };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        wiseTableColumnVisibility: {},
        wiseTableColumnOrder: {},
        wiseTableSelectedSettingsPresetId: {}
      };
    }
    const parsed = JSON.parse(raw) as Partial<IPersistedTraderPanelSlice>;
    return {
      wiseTableColumnVisibility: parsed.wiseTableColumnVisibility ?? {},
      wiseTableColumnOrder: parsed.wiseTableColumnOrder ?? {},
      wiseTableSelectedSettingsPresetId: parsed.wiseTableSelectedSettingsPresetId ?? {}
    };
  } catch {
    return {
      wiseTableColumnVisibility: {},
      wiseTableColumnOrder: {},
      wiseTableSelectedSettingsPresetId: {}
    };
  }
};

const persistState = (state: ITraderPanelSlice): void => {
  if (typeof window === 'undefined') return;
  try {
    const snapshot: IPersistedTraderPanelSlice = {
      wiseTableColumnVisibility: state.wiseTableColumnVisibility,
      wiseTableColumnOrder: state.wiseTableColumnOrder,
      wiseTableSelectedSettingsPresetId: state.wiseTableSelectedSettingsPresetId
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore quota / privacy mode
  }
};

const persisted = readPersistedState();

const initialState: ITraderPanelSlice = {
  parameters: {},
  wiseTableAdvancedFilters: {},
  wiseTableColumnVisibility: persisted.wiseTableColumnVisibility,
  wiseTableColumnOrder: persisted.wiseTableColumnOrder,
  wiseTableSelectedSettingsPresetId: persisted.wiseTableSelectedSettingsPresetId
};

export const traderPanelSlice = createSlice({
  name: 'traderPanel',
  initialState,
  reducers: {
    setTraderPanelParameters: (state, action: PayloadAction<{ componentSymbol: string; parameters: IComponentParameters }>) => {
      state.parameters[action.payload.componentSymbol] = action.payload.parameters;
    },

    addTraderPanelWiseTableAdvancedFilter: (
      state,
      action: PayloadAction<{ componentSymbol: string; filter: IWiseTableAdvancedFilter }>
    ) => {
      const { componentSymbol, filter } = action.payload;
      if (!componentSymbol) return;
      if (!Array.isArray(state.wiseTableAdvancedFilters[componentSymbol])) {
        state.wiseTableAdvancedFilters[componentSymbol] = [];
      }
      state.wiseTableAdvancedFilters[componentSymbol].push(filter);
    },

    setTraderPanelWiseTableAdvancedFilters: (
      state,
      action: PayloadAction<{ componentSymbol: string; filters: IWiseTableAdvancedFilter[] }>
    ) => {
      const { componentSymbol, filters } = action.payload;
      if (!componentSymbol) return;
      state.wiseTableAdvancedFilters[componentSymbol] = filters;
    },

    setTraderPanelWiseTableColumnVisibility: (
      state,
      action: PayloadAction<{
        componentSymbol: string;
        columnVisibility: Record<string, boolean>;
      }>
    ) => {
      const { componentSymbol, columnVisibility } = action.payload;
      if (!componentSymbol) return;
      state.wiseTableColumnVisibility[componentSymbol] = columnVisibility;
      persistState(state);
    },

    setTraderPanelWiseTableColumnOrder: (
      state,
      action: PayloadAction<{
        componentSymbol: string;
        columnOrder: string[];
      }>
    ) => {
      const { componentSymbol, columnOrder } = action.payload;
      if (!componentSymbol) return;
      state.wiseTableColumnOrder[componentSymbol] = columnOrder;
      persistState(state);
    },

    setTraderPanelWiseTableSelectedSettingsPresetId: (
      state,
      action: PayloadAction<{
        componentSymbol: string;
        presetId: string | null;
      }>
    ) => {
      const { componentSymbol, presetId } = action.payload;
      if (!componentSymbol) return;
      state.wiseTableSelectedSettingsPresetId[componentSymbol] = presetId;
      persistState(state);
    }
  }
});

export const traderPanelSliceActions = traderPanelSlice.actions;
export default traderPanelSlice.reducer;
